// Governance — the MCP scan decision queue. Every self-serve scan submission
// shows here (who asked, when, kind), marked against the active policy's grants:
// "granted" once whitelisted, otherwise "submitted" (awaiting scan/decision).
// Read-only from GET /api/v1/scans (admin-gated on the server).
import { useCallback, useEffect, useMemo, useState } from "react";
import { Tooltip } from "antd";
import Badge, { type BadgeVariant } from "@/components/Badge";
import PageHeader from "@/components/PageHeader";
import { useAuth } from "@/lib/auth/AuthProvider";

interface ScanRow {
  id: string;
  principal: string;
  kind: string; // "scan" | "dry_run"
  scan_id?: string;
  content_hash?: string;
  created_at: string;
  status: string; // "granted" | "submitted"
}

const STATUS_VARIANT: Record<string, BadgeVariant> = {
  granted: "success",
  submitted: "warning",
};

function fmtWhen(s: string): string {
  const d = new Date(s);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
}
function shortHash(h?: string): string {
  if (!h) return "—";
  const bare = h.replace(/^sha256:/, "");
  return bare.length > 12 ? `${bare.slice(0, 12)}…` : bare;
}

export default function Governance() {
  const { token } = useAuth();
  const [rows, setRows] = useState<ScanRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<"all" | "granted" | "submitted">("all");

  // Appends request_id (also the X-Request-Id response header) when present —
  // 500 bodies are deliberately generic, so it's the only thing a user can
  // hand support to correlate against.
  const explain = async (res: Response, fallback: string): Promise<string> => {
    try {
      const body = await res.json();
      const message = typeof body.detail === "string" ? body.detail : `${fallback} (${res.status})`;
      return typeof body.request_id === "string" ? `${message} (ref: ${body.request_id})` : message;
    } catch {
      return `${fallback} (${res.status})`;
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const res = await fetch(`${import.meta.env.BASE_URL}api/v1/scans`, {
        headers: { Authorization: `Bearer ${token ?? ""}` },
      });
      if (!res.ok) throw new Error(await explain(res, "Could not load scan queue"));
      const body = (await res.json()) as { items: ScanRow[] };
      setRows(body.items ?? []);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  const shown = useMemo(
    () => (filter === "all" ? rows : rows.filter((r) => r.status === filter)),
    [rows, filter],
  );
  const counts = useMemo(
    () => ({
      all: rows.length,
      granted: rows.filter((r) => r.status === "granted").length,
      submitted: rows.filter((r) => r.status === "submitted").length,
    }),
    [rows],
  );

  return (
    <div className="space-y-6">
      <PageHeader
        title="Governance"
        subtitle={
          <>Self-serve MCP scan submissions and their decisions. Passing scans a user
          whitelists show as <span className="text-success">granted</span>; the rest
          are awaiting a scan result or an admin exception.</>
        }
      />

      <div className="flex gap-2">
        {(["all", "submitted", "granted"] as const).map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={
              "rounded-md border px-3 py-1 text-sm capitalize " +
              (filter === f
                ? "border-accent bg-accent/10 text-accent"
                : "border-border text-text-muted hover:bg-surface")
            }
          >
            {f} <span className="text-text-muted">({counts[f]})</span>
          </button>
        ))}
      </div>

      {err && (
        <div className="rounded border border-danger/40 bg-danger/10 p-3 text-sm text-danger">
          {err}
        </div>
      )}

      <div className="rounded-lg border border-border bg-surface-elev">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-xs text-text-muted">
              <th className="px-4 py-2 font-medium">When</th>
              <th className="px-4 py-2 font-medium">Requested by</th>
              <th className="px-4 py-2 font-medium">Kind</th>
              <th className="px-4 py-2 font-medium">Artifact hash</th>
              <th className="px-4 py-2 font-medium">Status</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && shown.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-6 text-center text-text-muted">
                  No submissions.
                </td>
              </tr>
            )}
            {shown.map((r) => (
              <tr key={r.id} className="border-b border-border last:border-0">
                <td className="px-4 py-2 text-text-muted">{fmtWhen(r.created_at)}</td>
                <td className="px-4 py-2 font-medium">{r.principal}</td>
                <td className="px-4 py-2">
                  <Badge variant={r.kind === "dry_run" ? "default" : "info"}>
                    {r.kind === "dry_run" ? "dry run" : "scan"}
                  </Badge>
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  <Tooltip title={r.content_hash}>
                    <span>{shortHash(r.content_hash)}</span>
                  </Tooltip>
                </td>
                <td className="px-4 py-2">
                  <Badge variant={STATUS_VARIANT[r.status] ?? "default"}>
                    {r.status}
                  </Badge>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
